/**
 * @flow
 */

import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { pick } from 'lodash';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import uuid from 'uuid';
import { Icon } from 'semantic-ui-react';

import { Modal, Button } from '../../../components';
import SupplierForm from './form/SupplierForm';
import useUserLookup from '../../../lib/useUserLookup';
import { GET_SUPPLIERS_QUERY, CREATE_SUPPLIER_QUERY } from '../containers/queries';

const intlMessages = defineMessages({
    phoneExistingsError: {
        id: 'pages.suppliers.supplierForm.phoneExistingsError',
        defaultMessage: 'Supplier with the same phone number already exists',
    },
    emailExistingsError: {
        id: 'pages.suppliers.supplierForm.emailExistingsError',
        defaultMessage: 'Supplier with the same email already exists',
    },
    emailPhoneError: {
        id: 'pages.suppliers.supplierForm.emailPhoneError',
        defaultMessage:
            'There are two different users with the provided phone and email',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const DEFAULT_VALUES = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '+371',
    password: '',
    repeatPassword: '',
};

type CreateSupplierDialogProps = {
    trigger: Object,
    open: boolean,
    onClose: Function,
    onAdd?: Function,
    intl: Object,
};

function CreateSupplierDialog({ trigger, open, onClose, onAdd, intl }: CreateSupplierDialogProps) {
    const searchQuery = useSelector(({ suppliers }) => suppliers.get('searchQuery'));
    const { userLookupStatus, userLookupUser, onUserLookup, resetUserLookup } = useUserLookup();

    const methods = useForm({ defaultValues: DEFAULT_VALUES });
    const {
        handleSubmit,
        reset,
        setError,
        getValues,
        formState: { isSubmitting, isDirty },
    } = methods;

    // When userLookupUser changes (found existing user), fill in their fields
    useEffect(() => {
        if (open && userLookupUser) {
            const fields = pick(userLookupUser, ['firstName', 'lastName', 'email', 'phone']);
            reset({
                ...getValues(),
                firstName: fields.firstName || '',
                lastName: fields.lastName || '',
                email: fields.email || '',
                phone: fields.phone || '+371',
            });
        }
    }, [userLookupUser]); // eslint-disable-line react-hooks/exhaustive-deps

    const [createSupplier] = useMutation(CREATE_SUPPLIER_QUERY, {
        update: (store, { data: { createSupplier: result } }) => {
            try {
                const variables = {
                    search: searchQuery || undefined,
                    first: 20,
                    resolveRole: 'SYSADMIN',
                };
                const data = store.readQuery({
                    query: GET_SUPPLIERS_QUERY,
                    variables,
                });
                const edges = data.viewer.suppliers.edges || [];
                store.writeQuery({
                    query: GET_SUPPLIERS_QUERY,
                    variables,
                    data: {
                        ...data,
                        viewer: {
                            ...data.viewer,
                            suppliers: {
                                ...data.viewer.suppliers,
                                edges: [
                                    {
                                        node: result.supplier,
                                        __typename: 'SupplierEdge',
                                        cursor: btoa(result.supplier.id),
                                    },
                                    ...edges,
                                ],
                            },
                        },
                    },
                });
            } catch (e) {
                // cache not populated — safe to ignore
            }
        },
    });

    const onModalClose = () => {
        resetUserLookup();
        reset(DEFAULT_VALUES);
        onClose();
    };

    const handleClear = () => {
        resetUserLookup();
        reset(DEFAULT_VALUES);
    };

    const onSubmit = async formData => {
        const input = Object.assign(
            { phone: (formData.phone || '').replace(/[^+\d]/g, '') },
            pick(formData, ['firstName', 'lastName', 'email', 'password']),
        );

        try {
            const { data } = await createSupplier({
                variables: { input },
                optimisticResponse: {
                    __typename: 'Mutation',
                    createSupplier: {
                        __typename: 'CreateSupplierPayload',
                        supplier: {
                            __typename: 'Supplier',
                            ...input,
                            id: uuid.v4(),
                        },
                    },
                },
            });

            if (onAdd) {
                onAdd(data.createSupplier.supplier);
            }
            onModalClose();
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                if (graphQLError.name === 'AlreadyExists') {
                    if (graphQLError.data?.error === 'PHONE_ALREADY_EXISTS') {
                        setError('phone', { message: intl.formatMessage(intlMessages.phoneExistingsError) });
                        return;
                    } else if (graphQLError.data?.error === 'EMAIL_ALREADY_EXISTS') {
                        setError('email', { message: intl.formatMessage(intlMessages.emailExistingsError) });
                        return;
                    }
                } else if (graphQLError.name === 'Forbidden') {
                    if (graphQLError.data?.error === 'THERE_ARE_TWO_DIFFERENT_USER_WITH_PROVIDED_PHONE_AND_EMAIL') {
                        setError('root', { message: intl.formatMessage(intlMessages.emailPhoneError) });
                        return;
                    }
                }
                setError('root', { message: graphQLError.message || intl.formatMessage(intlMessages.defaultErrorMessage) });
                return;
            }
            setError('root', { message: intl.formatMessage(intlMessages.defaultErrorMessage) });
        }
    };

    const readOnly = userLookupStatus === 'EXISTS';

    // Pristine check: same as DEFAULT_VALUES
    const checkFormPristine = () => !isDirty;

    return (
        <FormProvider {...methods}>
            <Modal trigger={trigger} onClose={onModalClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.suppliers.createSupplier.newSupplier"
                        defaultMessage="New Supplier"
                    />
                </Modal.Header>
                <Modal.Content>
                    <SupplierForm
                        readOnly={readOnly}
                        userLookupStatus={userLookupStatus}
                        userLookupUser={userLookupUser}
                        onUserLookup={onUserLookup}
                        resetUserLookup={resetUserLookup}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        onClick={handleSubmit(onSubmit)}
                        disabled={isSubmitting || checkFormPristine()}
                        loading={isSubmitting}
                        primary
                    >
                        <FormattedMessage
                            id="pages.suppliers.createSupplier.add"
                            defaultMessage="Add"
                        />
                    </Button>
                    <Button onClick={handleClear} disabled={isSubmitting} basic>
                        <FormattedMessage
                            id="pages.suppliers.createSupplier.clear"
                            defaultMessage="Clear"
                        />
                    </Button>
                    <Button onClick={onModalClose} disabled={isSubmitting} basic>
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.suppliers.createSupplier.cancel"
                            defaultMessage="Cancel"
                        />
                    </Button>
                </Modal.Actions>
            </Modal>
        </FormProvider>
    );
}

export default injectIntl(CreateSupplierDialog);
